import { Box, DialogActions, DialogContent, IconButton, Typography } from "@mui/material"
import { CloseOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import type { FC, ReactElement } from "react"

import { Button } from "@/components/common/Button"

export interface ConfirmationTemplateProps {
  title: string
  content: ReactElement | string
  closeLabel: string
  confirmLabel: string
  isWarning?: boolean
  confirmDisabled?: boolean
  testIdPrefix?: string
  closeButtonTestIdSuffix?: string
  confirmButtonTestIdSuffix?: string
  onClose: () => void
  onConfirm: () => void
}

export const ConfirmationTemplate: FC<ConfirmationTemplateProps> = ({
  title,
  content,
  closeLabel,
  confirmLabel,
  isWarning = false,
  confirmDisabled = false,
  testIdPrefix,
  closeButtonTestIdSuffix = "close-button",
  confirmButtonTestIdSuffix = "submit-button",
  onConfirm,
  onClose,
}) => {
  const titleTestId = testIdPrefix ? `${testIdPrefix}.title` : "confirmation-title"
  const contentTestId = testIdPrefix ? `${testIdPrefix}.content` : "confirmation-content"
  const closeIconTestId = testIdPrefix ? `${testIdPrefix}.close-icon-button` : "confirmation-close-icon-button"
  const closeButtonTestId = testIdPrefix ? `${testIdPrefix}.${closeButtonTestIdSuffix}` : "confirmation-close-button"
  const submitButtonTestId = testIdPrefix
    ? `${testIdPrefix}.${confirmButtonTestIdSuffix}`
    : "confirmation-submit-button"

  return (
    <>
      <Box className='flex justify-end px-4 pt-4'>
        <IconButton data-testid={closeIconTestId} size='small' onClick={onClose}>
          <CloseOutlined className='text-blue-gray' />
        </IconButton>
      </Box>
      <DialogContent classes={{ root: "px-10 pt-0 pb-10" }}>
        <Box className='mb-6 w-full justify-center text-center'>
          <WarningOutlined className='size-[48px] text-secondary-600' />
          <Typography
            data-testid={titleTestId}
            color='primary'
            className='mt-6 text-xl font-normal leading-[133%] tracking-[-1px]'
            component='h2'
          >
            {title}
          </Typography>
        </Box>
        <Typography
          data-testid={contentTestId}
          className='mt-6 px-3 text-center text-base leading-[150%] tracking-[-0.85px] text-gray-700'
          component='div'
        >
          {content}
        </Typography>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button data-testid={closeButtonTestId} appearance='outlined' onClick={onClose}>
          {closeLabel}
        </Button>
        <Button
          data-testid={submitButtonTestId}
          variant={isWarning ? "error" : "primary"}
          disabled={confirmDisabled}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </>
  )
}
